import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { Autocomplete, IconButton } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { fetchModelByDropDown } from "slices/Masters/model/thunk";
import { fetchProductsDropDownByModel } from "slices/Masters/product/thunk";
import { MecordColors } from "Constants/Color";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { useFormik } from "formik";
import * as Yup from "yup";
import EditButton from "Common/Components/Buttons/EditButton";
import { DeleteButton } from "Common/Components/Buttons/DeleteButton";
import { autocompleteDropdownStyles } from "Constants/Style";
import { showCustomSwal } from "helpers/swalHelper";
import { Add } from "@mui/icons-material";
interface Props {
  mainFormik: any;
}

type FormValues = {
  model: any;
  product: any;
  product_code: string;
  quantity: string | number;
  description: string;
};

interface ModelOption {
  model_id: string;
  model_name: string;
}

interface ProductOption {
  product_id: string;
  product_name: string;
  unit_id?: string;
  [key: string]: any;
}

// Validation Schema

const ProductFormTable: React.FC<Props> = ({ mainFormik }) => {
  const dispatch = useDispatch<any>();
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [modelOptions, setModelOptions] = useState<ModelOption[]>([]);
  const [dropDownLoading, setDropDownLoading] = useState<boolean>(false);
  const [editableRowId, setEditableRowId] = useState<string | null>(null);

  // Get products from mainFormik
  const items = mainFormik.values.products || [];

  // Formik setup
  const formik = useFormik<FormValues>({
    initialValues: {
      model: null,
      product: null,
      product_code: "",
      quantity: "",
      description: "",
    },
    validationSchema: Yup.object().shape({
      model: Yup.object().nullable().required("Model is required"),
      product: Yup.object().nullable().required("Product is required"),
      product_code: Yup.string().required("Product Code is required"),
      quantity: Yup.string()
        .required("Quantity is required")
        .test("is-positive", "Quantity must be a positive number", (value) => {
          if (!value) return false;
          const num = parseFloat(value);
          return !isNaN(num) && num > 0;
        }),
      description: Yup.string(),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      mainFormik.setFieldValue("products", [
        ...mainFormik?.values?.products,
        { ...values },
      ]);
      formik.resetForm();
    },
  });

  // Fetch models on mount
  useEffect(() => {
    async function loadModels() {
      setDropDownLoading(true);
      try {
        const modelRes = await dispatch(fetchModelByDropDown()).unwrap();
        setModelOptions(modelRes?.data || []);
      } catch (error) {
        toast.error("Failed to load models");
      } finally {
        setDropDownLoading(false);
      }
    }
    loadModels();
  }, [dispatch]);

  const fetchProducts = async (model_id: string) => {
    try {
      const response = await dispatch(
        fetchProductsDropDownByModel(model_id)
      ).unwrap();
      setProductOptions(response?.data || []);
    } catch (error) {
      toast.error("Failed to load products");
      setProductOptions([]);
    }
  };

  const handleDelete = async (index: number) => {
    const product = items[index];
    const productName = product?.product?.product_name || product?.product_name || "this product";

    const result = await showCustomSwal({
      icon: "warning",
      title: "Delete Product",
      text: `Are you sure you want to delete ${productName}? This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });

    if (result.isConfirmed) {
      const updated = [...items];
      updated.splice(index, 1);
      mainFormik.setFieldValue("products", updated);

      showCustomSwal({
        icon: "success",
        title: "Deleted!",
        text: "The product has been removed successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  return (
    <div
      className="p-3 rounded bg-opacity-10 mb-1"
      style={{
        background: MecordColors?.LightThemeBGColor,
        border: `1px solid ${MecordColors?.LightThemeBGColor}`,
      }}
    >
      <Form>
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Model <span className="text-danger">*</span></Form.Label>
              <Autocomplete
                options={modelOptions}
                loading={dropDownLoading}
                getOptionLabel={(option: any) => option?.model_name || ""}
                isOptionEqualToValue={(option: any, value: any) =>
                  option?.model_id === value?.model_id
                }
                value={formik?.values?.model}
                onChange={(e, newValue) => {
                  formik.setFieldValue("model", newValue);
                  // Reset all dependent fields when model changes
                  formik.setFieldValue("product", null);
                  formik.setFieldValue("product_code", "");
                  formik.setFieldValue("quantity", "");
                  formik.setFieldValue("description", "");
                  if (newValue) {
                    fetchProducts(newValue?.model_id);
                  } else {
                    setProductOptions([]);
                  }
                }}
                onBlur={() => formik.setFieldTouched("model", true)}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.model_name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Model"
                    size="small"
                    error={Boolean(formik.touched.model && formik.errors.model)}
                    helperText={
                      typeof formik.errors.model === "string" &&
                        formik.errors.model &&
                        formik.touched.model
                        ? formik.errors.model
                        : ""
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                  />
                )}
              />
            </Form.Group>
          </div>

          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Product <span className="text-danger">*</span></Form.Label>
              <Autocomplete
                options={productOptions}
                getOptionLabel={(opt: any) => opt?.product_name || ""}
                isOptionEqualToValue={(option: any, value: any) =>
                  option?.product_id === value?.product_id
                }
                value={formik?.values?.product}
                onChange={(e, newValue) => {
                  formik.setFieldValue("product", newValue);
                  // Reset dependent fields when product changes
                  formik.setFieldValue("product_code", "");
                  formik.setFieldValue("quantity", "");
                  formik.setFieldValue("description", "");
                }}
                onBlur={() => formik.setFieldTouched("product", true)}
                disabled={!formik?.values?.model}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.product_name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder={
                      formik?.values?.model
                        ? "Select Product"
                        : "Select Model first"
                    }
                    size="small"
                    error={Boolean(
                      !!formik.errors.product && formik.touched.product
                    )}
                    helperText={
                      typeof formik.errors.product === "string" &&
                        formik.errors.product &&
                        formik.touched.product
                        ? formik.errors.product
                        : ""
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                  />
                )}
              />
            </Form.Group>
          </div>

          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Quantity <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="quantity"
                placeholder="Enter Quantity"
                value={formik.values.quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow numbers and decimal point
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    formik.setFieldValue("quantity", value);
                  }
                }}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.quantity && formik.touched.quantity}
                style={
                  formik.errors.quantity && formik.touched.quantity
                    ? { borderColor: "red" }
                    : {}
                }
              />
              {formik.errors.quantity && formik.touched.quantity && (
                <Form.Control.Feedback type="invalid">
                  {formik.errors.quantity}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </div>
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Product Code <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="product_code"
                placeholder="Enter Product Code"
                value={formik.values.product_code}
                onChange={(e) => {
                  const value = e.target.value;

                  formik.setFieldValue("product_code", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.product_code && formik.touched.product_code}
                style={
                  formik.errors.product_code && formik.touched.product_code
                    ? { borderColor: "red" }
                    : {}
                }
              />
              {formik.errors.product_code && formik.touched.product_code && (
                <Form.Control.Feedback type="invalid">
                  {formik.errors.product_code}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </div>


          <div className="d-flex justify-content-between align-items-end">
            <div className="col-md-9">
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  name="description"
                  placeholder="Enter Description"
                  value={formik.values.description || ""}
                  onChange={(e) => {
                    formik.setFieldValue("description", e.target.value);
                  }}
                  onBlur={formik.handleBlur}
                />
              </Form.Group>
            </div>
            <Button
              variant="primary"
              onClick={() => formik.handleSubmit()}
              className="d-flex align-items-center justify-content-center gap-2"
              style={{
                backgroundColor: MecordColors.PrimaryButtonBGColor,
                borderColor: MecordColors.PrimaryButtonBGColor,
                borderRadius: "8px",
                height: "40px",
                width: "100px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor =
                  MecordColors.PrimaryButtonHoverBGColor;
                e.currentTarget.style.borderColor =
                  MecordColors.PrimaryButtonHoverBGColor;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor =
                  MecordColors.PrimaryButtonBGColor;
                e.currentTarget.style.borderColor =
                  MecordColors.PrimaryButtonBGColor;
              }}
            >
              <Add fontSize="small" />
              Add
            </Button>

          </div>
        </div>
      </Form>

      {/* Table */}
      <div
        className="table-responsive mb-3"
        style={{
          background: "#FFFFFF",
          border: "1px solid #DDE1EF",
          borderRadius: "5px",
        }}
      >
        <table
          className="table align-middle"
          style={{ minWidth: "1260px" }}
        >
          <thead style={{ backgroundColor: MecordColors?.LightGrayColor }}>
            <tr>
              <th style={{ minWidth: "80px" }}>Sr No.</th>
              <th style={{ minWidth: "200px" }}>Product</th>
              <th style={{ minWidth: "160px" }}>Product Code</th>
              <th style={{ minWidth: "160px" }}>Category</th>
              <th style={{ minWidth: "200px" }}>Sub Category</th>
              <th style={{ minWidth: "120px" }}>Unit</th>
              <th style={{ minWidth: "120px" }}>Unit Price</th>
              <th style={{ minWidth: "100px" }}>Quantity</th>
              <th style={{ minWidth: "120px" }}>Make</th>
              <th style={{ minWidth: "120px" }}>Model</th>
              <th style={{ minWidth: "200px" }}>Description</th>
              <th
                style={{
                  minWidth: "100px",
                  textAlign: "center",
                  position: "sticky",
                  right: 0,
                  backgroundColor: MecordColors?.LightGrayColor,
                  zIndex: 10,
                }}
                className="sticky-column"
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item: any, index: number) => {
                // Helper function to update item state and recalculate values
                const rowId = item.product?.product_id ?? `row-${index}`;

                const handleItemUpdate = (
                  field: string,
                  value: string | number
                ) => {
                  const updatedProducts = [...items];
                  const updatedProduct = { ...updatedProducts[index] };

                  if (field === "quantity") {
                    updatedProduct.quantity = parseFloat(value.toString()) || 0;
                  } else if (field === "description") {
                    updatedProduct.description = value.toString();
                  } else if (field === "product_code") {
                    updatedProduct.product_code = value.toString();
                  }

                  updatedProducts[index] = updatedProduct;
                  mainFormik.setFieldValue("products", updatedProducts);
                };

                return (
                  <tr key={rowId}>
                    <td style={{ width: "60px" }}>{index + 1}</td>
                    <td style={{ width: "200px" }}>
                      {item?.product?.product_name || "-"}
                    </td>
                    <td style={{ width: "160px" }}>
                      {editableRowId !== rowId ? (
                        <span>{item.product_code || "-"}</span>
                      ) : (
                        <Form.Control
                          type="text"
                          value={item.product_code || ""}
                          readOnly={editableRowId !== rowId}
                          onChange={(e) => {
                            if (editableRowId === rowId) {
                              handleItemUpdate("product_code", e.target.value);
                            }
                          }}
                          placeholder="Enter product code"
                        />
                      )}
                    </td>
                    <td style={{ width: "200px" }}>
                      {item?.product?.category?.category_name || "-"}
                    </td>
                    <td style={{ width: "200px" }}>
                      {item?.product?.sub_category?.sub_category_name || "-"}
                    </td>
                    <td style={{ width: "120px" }}>
                      {item?.product?.unit?.unit_name || "-"}
                    </td>
                    <td style={{ width: "120px" }}>
                      {item?.product?.unit_price || item?.unit_price || "-"}
                    </td>
                    <td style={{ width: "100px" }}>
                      {editableRowId !== rowId ? (
                        <span>{item.quantity || "-"}</span>
                      ) : (
                        <Form.Control
                        type="text"
                        value={item.quantity || ""}
                        readOnly={editableRowId !== rowId}
                        onChange={(e) => {
                          if (editableRowId === rowId) {
                            const value = e.target.value;
                            // Only allow numbers and decimal point
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              handleItemUpdate("quantity", value);
                            }
                          }
                        }}
                      />
                      )}
                    </td>
                    <td>{item?.product?.make?.make_name || "-"}</td>
                    <td>{item?.product?.model?.model_name || ""}</td>
                    <td style={{ width: "200px" }}>
                      {editableRowId !== rowId ? (
                        <span>{item.description || "-"}</span>
                      ) : (
                        <Form.Control
                        type="text"
                        value={item.description || ""}
                        readOnly={editableRowId !== rowId}
                        onChange={(e) => {
                          if (editableRowId === rowId) {
                            handleItemUpdate("description", e.target.value);
                          }
                        }}
                        placeholder="Enter description"
                        />
                      )}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#FFFFFF",
                        zIndex: 10,
                      }}
                      className="sticky-column"
                    >
                      {editableRowId === rowId ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <IconButton
                            onClick={() => {
                              setEditableRowId(null);
                              toast.success("Row updated!");
                            }}
                            size="small"
                            sx={{
                              color: MecordColors?.ThemeBGColor || "#0d6efd",
                              "&:hover": {
                                backgroundColor: `${MecordColors?.ThemeBGColor || "#0d6efd"  
                                  }15`,
                              },
                            }}
                            title="Save"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setEditableRowId(null);
                            }}
                            size="small"
                            sx={{
                              color: "#6c757d",
                              "&:hover": {
                                backgroundColor: "#f8f9fa",
                              },
                            }}
                            title="Cancel"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <EditButton
                            onClick={() => {
                              setEditableRowId(rowId);
                            }}
                            iconClassName="ph-pencil"
                          />
                          <DeleteButton
                            onClick={() => {
                              handleDelete(index);
                            }}
                            iconClassName="ph-trash"
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} className="text-center py-5 text-muted">
                  No items added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductFormTable;
